import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, CheckCheck, Gift, AlertTriangle, Users, MessageSquare, X, SlidersHorizontal, ArrowLeft, Heart, Calendar, Signature } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

// Format a notification's created_at safely — the server may occasionally
// serve rows with a missing / malformed timestamp during backfills or when
// a webhook writes early; render an em-dash instead of "Invalid Date".
function formatNotifTime(raw) {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

const KIND_ICON = {
  deadline_missed: AlertTriangle,
  referral_reward: Gift,
  team_invite_accepted: Users,
  client_message: MessageSquare,
  hot_lead_saves: Heart,
  showing_request: Calendar,
  document_signed: Signature,
};

const KIND_COLOR = {
  deadline_missed: "text-rose-600 bg-rose-50",
  referral_reward: "text-emerald-600 bg-emerald-50",
  team_invite_accepted: "text-slate-700 bg-slate-100",
  client_message: "text-sky-600 bg-sky-50",
  hot_lead_saves: "text-pink-600 bg-pink-50",
  showing_request: "text-amber-600 bg-amber-50",
  document_signed: "text-emerald-700 bg-emerald-50",
};

const KIND_LABEL = {
  deadline_missed: "Missed deadlines",
  referral_reward: "Referral rewards",
  team_invite_accepted: "Team joins",
  client_message: "Client messages",
  hot_lead_saves: "Hot leads (2+ saves)",
  showing_request: "Showing requests",
  document_signed: "Signed documents",
};

const KIND_DESC = {
  deadline_missed: "Ping me when a contract deadline slips past due.",
  referral_reward: "Tell me when an invited agent starts a paid subscription.",
  team_invite_accepted: "Alert me when a new agent joins my brokerage.",
  client_message: "Notify me every time a client sends me a message.",
  hot_lead_saves: "Alert me the first time a lead saves 2 properties in their portal.",
  showing_request: "Ping me the moment a client requests a showing.",
  document_signed: "Ring the alarm when a countersigned PDF lands on a transaction.",
};

/**
 * Bell icon + unread badge + slide-in drawer.
 * Polls every 60s while the app is open. When drawer opens, marks the visible
 * items as read via mark-as-read on click OR bulk read-all button.
 */
export default function NotificationBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list"); // "list" | "prefs"
  const [prefs, setPrefs] = useState(null);
  // "ring" flag flips true for ~1.6s when we detect a brand-new signed
  // document. Drives the bell shake animation + a toast so the agent
  // knows the file has arrived without having to open the drawer.
  const [ring, setRing] = useState(false);
  // Track ids we've already alerted on so the toast/shake doesn't
  // re-fire on every 20s poll — only when a genuinely new signed doc
  // appears.
  const seenSignedIds = useRef(new Set());
  const bootstrapped = useRef(false);
  const timerRef = useRef(null);

  const loadPrefs = async () => {
    try {
      const { data } = await api.get("/notifications/prefs");
      // Defensive: the API is expected to return a plain object of
      // `{ kind: boolean }` toggles. Reject anything else (array, null,
      // scalar) so the toggles UI never crashes on a bad payload.
      setPrefs(data && typeof data === "object" && !Array.isArray(data) ? data : {});
    } catch (err) { console.error("[NotificationBell] loadPrefs failed", err); }
  };

  const togglePref = async (kind) => {
    const next = { ...prefs, [kind]: !prefs[kind] };
    setPrefs(next); // optimistic
    try {
      const { data } = await api.put("/notifications/prefs", { [kind]: next[kind] });
      setPrefs(data);
    } catch (err) {
      console.error("[NotificationBell] togglePref failed", err);
      setPrefs(prefs); // rollback
    }
  };

  const drawerRef = useRef(null);
  const bellRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 25 } });
      // Defensive: `items` should always be an array and `unread_count`
      // a number, but 502s / partial JSON / an unauthenticated 401 can
      // leave `data` undefined or a stray HTML error page. Guard so
      // `items.map(...)` and the badge never crash.
      const nextItems = Array.isArray(data?.items) ? data.items : [];
      setItems(nextItems);
      setUnread(data?.unread_count ?? 0);
      // Ring-the-alarm: on any poll that surfaces a brand-new UNREAD
      // `document_signed` row we haven't seen before, shake the bell
      // and fire a toast. Bootstrap pass just seeds `seenSignedIds`
      // so the very first poll doesn't spam a toast for every historical
      // signed row already sitting in the feed.
      const signedRows = nextItems.filter(n => n.kind === "document_signed");
      if (!bootstrapped.current) {
        signedRows.forEach(n => seenSignedIds.current.add(n.id));
        bootstrapped.current = true;
        return;
      }
      const brandNew = signedRows.filter(n => !n.read && !seenSignedIds.current.has(n.id));
      brandNew.forEach(n => seenSignedIds.current.add(n.id));
      if (brandNew.length) {
        setRing(true);
        setTimeout(() => setRing(false), 1600);
        const first = brandNew[0];
        toast.success(first.title, {
          description: first.body,
          action: first.link ? { label: "Open", onClick: () => nav(first.link) } : undefined,
          duration: 8000,
        });
      }
    } catch (err) {
      console.error("[NotificationBell] load failed", err);
    }
  };

  useEffect(() => {
    load();
    // Poll every 20s so a countersigned PDF hitting the webhook shows up
    // on the bell within roughly the same window as the DocuSign email
    // used to arrive — but without the inbox noise.
    timerRef.current = setInterval(load, 20_000);
    return () => timerRef.current && clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape-to-close + focus-trap while open. Restore focus to the bell on close.
  useEffect(() => {
    if (!open) return;
    const drawer = drawerRef.current;
    // Focus the drawer container so keyboard nav starts there.
    if (drawer) drawer.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); return; }
      if (e.key !== "Tab" || !drawer) return;
      const focusable = drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Restore focus to bell after close.
  useEffect(() => {
    if (!open && bellRef.current && document.activeElement === document.body) {
      bellRef.current.focus();
    }
  }, [open]);

  const clickItem = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
        setUnread(u => Math.max(0, u - 1));
      } catch (err) { console.error("[NotificationBell] mark-read failed", err); }
    }
    setOpen(false);
    if (n.link) nav(n.link);
  };

  const readAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems(prev => prev.map(x => ({ ...x, read: true })));
      setUnread(0);
    } catch (err) { console.error("[NotificationBell] read-all failed", err); }
  };

  return (
    <>
      <button
        ref={bellRef}
        onClick={() => { setView("list"); setOpen(true); }}
        className={`relative inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-slate-100 ${ring ? "text-rose-600 bell-ring" : "text-slate-500 hover:text-slate-900"}`}
        data-testid="nav-bell"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}${ring ? " — new activity" : ""}`}
      >
        {ring ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
        {unread > 0 && (
          <span className={`absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center tabular-nums ${ring ? "bg-rose-600 ring-2 ring-rose-200" : "bg-rose-500"}`} data-testid="bell-unread-count">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-40" data-testid="notifications-drawer" role="dialog" aria-modal="true" aria-label="Notifications">
          <div className="absolute inset-0 bg-slate-900/30" onClick={() => setOpen(false)} />
          <div ref={drawerRef} tabIndex={-1} className="absolute right-0 top-0 h-full w-full max-w-sm bg-white shadow-xl flex flex-col outline-none">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              {view === "prefs" ? (
                <>
                  <button onClick={() => setView("list")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900" data-testid="notif-prefs-back">
                    <ArrowLeft className="h-4 w-4" /> Preferences
                  </button>
                  <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-slate-100" data-testid="notif-close">
                    <X className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <div>
                    <h2 className="font-heading text-lg font-bold text-slate-900">Notifications</h2>
                    <p className="text-xs text-slate-500">{unread} unread</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setView("prefs"); if (!prefs) loadPrefs(); }}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                      title="Notification preferences"
                      data-testid="notif-prefs-btn"
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                    </button>
                    {unread > 0 && (
                      <button onClick={readAll} className="text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="notif-read-all">
                        <CheckCheck className="h-3.5 w-3.5" /> Read all
                      </button>
                    )}
                    <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-slate-100" data-testid="notif-close">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {view === "prefs" ? (
                <div className="p-5 space-y-1" data-testid="notif-prefs-list">
                  <p className="text-xs text-slate-500 mb-4">Toggle a category off to stop it from lighting up your bell. Missed-deadline emails still send regardless — this only affects in-app notifications.</p>
                  {prefs === null && <div className="text-sm text-slate-500">Loading…</div>}
                  {prefs !== null && Object.keys(KIND_LABEL).map(kind => {
                    const enabled = prefs[kind] !== false;
                    return (
                      <label
                        key={kind}
                        className="flex items-start justify-between gap-4 py-3 border-b border-slate-100 last:border-b-0 cursor-pointer"
                        data-testid={`pref-row-${kind}`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900">{KIND_LABEL[kind]}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{KIND_DESC[kind]}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => togglePref(kind)}
                          className={`flex-shrink-0 relative w-10 h-6 rounded-full transition-colors ${enabled ? "bg-emerald-500" : "bg-slate-300"}`}
                          role="switch" aria-checked={enabled}
                          data-testid={`pref-toggle-${kind}`}
                        >
                          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                        </button>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <>
                  {items.length === 0 && (
                    <div className="p-12 text-center" data-testid="notif-empty">
                      <Bell className="h-10 w-10 mx-auto text-slate-300" />
                      <p className="mt-3 text-sm text-slate-500">You're all caught up.</p>
                    </div>
                  )}
                  {items.map(n => {
                    const Icon = KIND_ICON[n.kind] || Bell;
                    const colorCls = KIND_COLOR[n.kind] || "text-slate-600 bg-slate-100";
                    return (
                      <button
                        key={n.id}
                        onClick={() => clickItem(n)}
                        className={`w-full text-left px-5 py-4 border-b border-slate-100 hover:bg-slate-50 flex gap-3 ${!n.read ? "bg-blue-50/40" : ""}`}
                        data-testid={`notif-${n.id}`}
                      >
                        <div className={`flex-shrink-0 h-8 w-8 rounded-md ${colorCls} flex items-center justify-center`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{n.title}</div>
                          {n.body && <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.body}</div>}
                          <div className="text-[10px] text-slate-400 mt-1">{formatNotifTime(n.created_at)}</div>
                        </div>
                        {!n.read && <span className="mt-1 h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" aria-label="unread" />}
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
